import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaLink, FaArrowRight } from 'react-icons/fa';
import { SOURCE_NAME } from '../constants/tagConstants';
import { detectLookupMode } from '../utils/lookupUtils';

// Prominent, home-page-only search/add region. Mirrors the PaperLookup modal
// (cmd+k) but lives inline so first-time visitors discover the dual-mode
// behavior without needing to guess at the small header button.
function PaperSearchHero() {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();

  const mode = detectLookupMode(input);
  const canSubmit = mode !== 'empty';

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    if (mode === 'id') {
      navigate(`/paper/${trimmed}`);
    } else {
      navigate(`/today?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const borderCls = mode === 'id'
    ? 'border-purple-300/60 shadow-[0_0_0_4px_rgba(168,85,247,0.12)]'
    : mode === 'keyword'
      ? 'border-yellow-300/60 shadow-[0_0_0_4px_rgba(253,224,71,0.15)]'
      : focused
        ? 'border-white/30'
        : 'border-white/10';

  const submitColor = mode === 'id'
    ? 'bg-purple-500 hover:bg-purple-400 text-white'
    : 'bg-yellow-300 hover:bg-yellow-200 text-gray-900';

  return (
    <motion.section
      className="mb-16"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
      aria-label="Paper search and add"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] text-yellow-300/70 uppercase tracking-[0.25em] font-semibold">
          // find or add
        </span>
        <div className="flex-1 h-px bg-white/10" />
        <span className="hidden sm:inline text-[10px] text-white/30 uppercase tracking-widest font-semibold">
          <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[9px] font-mono text-white/60 border border-white/10">⌘K</kbd>
          <span className="ml-1.5">anywhere</span>
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className={`glass-strong rounded-2xl p-5 sm:p-6 border-2 transition-all duration-200 ${borderCls}`}>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200"
                 style={{
                   background: mode === 'id'
                     ? 'rgba(168, 85, 247, 0.2)'
                     : mode === 'keyword'
                       ? 'rgba(253, 224, 71, 0.2)'
                       : 'rgba(255, 255, 255, 0.08)'
                 }}
            >
              {mode === 'id'
                ? <FaLink className="text-purple-300 text-sm" />
                : <FaSearch className={mode === 'keyword' ? 'text-yellow-300 text-sm' : 'text-white/40 text-sm'} />
              }
            </div>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={`Paste a ${SOURCE_NAME} ID, URL, or search keywords…`}
              className="flex-1 bg-transparent text-base sm:text-lg text-white placeholder-white/30 focus:outline-none tracking-tight"
            />
            <motion.button
              type="submit"
              disabled={!canSubmit}
              whileHover={canSubmit ? { scale: 1.03 } : {}}
              whileTap={canSubmit ? { scale: 0.97 } : {}}
              className={`flex-shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-3 sm:py-2.5 rounded-lg text-sm sm:text-xs font-bold uppercase tracking-wider transition-colors duration-200 min-h-[44px] sm:min-h-0 ${submitColor} ${!canSubmit ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {mode === 'id' ? 'Fetch' : 'Search'}
              <FaArrowRight className="text-[10px]" />
            </motion.button>
          </div>
        </div>
      </form>

      {/* Two explainer tiles — visible at all times so users know both modes exist,
          and dim/highlight based on the current input to show which one is live. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <ExplainerTile
          active={mode === 'id'}
          dim={mode === 'keyword'}
          tone="purple"
          icon={FaLink}
          title="Direct fetch"
          sub="by ID, DOI, or URL"
          example={`e.g. 2401.12345 · arxiv.org/abs/…`}
          hint={`Adds the paper to paperdigest — even if it wasn't in today's digest.`}
        />
        <ExplainerTile
          active={mode === 'keyword'}
          dim={mode === 'id'}
          tone="yellow"
          icon={FaSearch}
          title="Keyword search"
          sub="across today's papers"
          example={`e.g. "diffusion models" · "graph neural"`}
          hint={`Filters by title, topic, and abstract in today's ${SOURCE_NAME} digest.`}
        />
      </div>
    </motion.section>
  );
}

function ExplainerTile({ active, dim, tone, icon: Icon, title, sub, example, hint }) {
  const toneCls = tone === 'purple'
    ? {
        dot: 'bg-purple-400',
        ring: 'border-purple-400/60 bg-purple-500/10',
        title: 'text-purple-200',
      }
    : {
        dot: 'bg-yellow-300',
        ring: 'border-yellow-300/60 bg-yellow-400/10',
        title: 'text-yellow-200',
      };
  return (
    <div className={`rounded-xl p-4 border transition-all duration-200 ${
      active ? toneCls.ring : 'border-white/10 bg-white/[0.04]'
    } ${dim ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-1.5 h-1.5 rounded-full ${toneCls.dot} ${active ? '' : 'opacity-50'}`} />
        <Icon className={`text-[10px] ${active ? toneCls.title : 'text-white/40'}`} />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${active ? toneCls.title : 'text-white/50'}`}>
          {title}
        </span>
        <span className="text-[10px] text-white/30 font-medium truncate">{sub}</span>
      </div>
      <p className="text-[11px] text-white/50 leading-relaxed mb-1.5 font-mono tracking-tight">{example}</p>
      <p className="text-[11px] text-white/40 leading-relaxed">{hint}</p>
    </div>
  );
}

export default PaperSearchHero;
